import { Menu } from "lucide-react";
import {
  Breadcrumbs,
  BreadcrumbsProvider,
  useBreadcrumbItems,
} from "@/components/shared/breadcrumbs";
import { OriginProvider } from "@/components/shared/origin-context";
import { Button } from "@/components/ui/button";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { Sidebar } from "./Sidebar";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import { TestEmailBanner } from "./TestEmailBanner";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";

function DashboardLayoutInner({ children }: { children: React.ReactNode }) {
  const { isMobile, setMobileOpen } = useSidebar();
  const crumbs = useBreadcrumbItems();

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />

      <div className="flex flex-1 flex-col overflow-hidden">
        <ImpersonationBanner />
        <TestEmailBanner />
        {/* Header */}
        <header className="flex h-16 items-center justify-between border-b border-border bg-background px-4 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(true)}
                className="shrink-0"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            {isMobile ? (
              <span className="font-display text-lg font-bold truncate">Nos2 Enable</span>
            ) : (
              <Breadcrumbs items={crumbs} />
            )}
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Widened from max-w-5xl (64rem) to give data more room — trims the
              left/right whitespace by ~25% on a typical wide screen.
              A page with a wide grid on it (e.g. the role expectations table,
              which is unreadable squeezed into 72rem with every rating column
              showing) opts out by rendering anything with `data-wide-page`
              somewhere inside — no prop threading through every route. The cap
              still applies, so narrow windows are unaffected. */}
          <div className="mx-auto w-full max-w-6xl has-[[data-wide-page]]:max-w-[100rem]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <BreadcrumbsProvider>
        <OriginProvider>
          <DashboardLayoutInner>{children}</DashboardLayoutInner>
        </OriginProvider>
      </BreadcrumbsProvider>
    </SidebarProvider>
  );
}
